/**
 * 계정 시드 - 봉투 스트림의 뿌리를 **한 번** 만든다.
 *
 * 근거 문서: 로드맵 확정안 3.2절(봉투 - 결정론) · 부록("엔트로피는 `app/accountSeed.ts`·`server/src/roomCodes.ts` 둘")
 *           `이상한방-상태모델-v0.1.md` 11.4절(예측의 ROI는 음수) · 난수 계약은 `core/rng.ts` 머리말
 *
 * ## 이 파일이 클라이언트에서 엔트로피를 쓰는 유일한 자리다
 *
 * `rng.ts`가 `Math.random()`을 전면 금지한 이유는 재현성이다 - 방·변화·CPU 행동·봉투가 전부 시드에서
 * 파생돼야 버그 재현과 `?pull=seq`가 성립한다. 그런데 **시드 자체**는 어디선가 한 번 태어나야 하고,
 * 그 한 번이 여기다. 첫 실행에 `crypto.getRandomValues`로 16바이트를 뽑아 진행 저장(v3)에 넣고,
 * 그 뒤로는 저장된 값을 읽기만 한다. 두 번째 엔트로피 사용처가 생기면 그건 "재현되지 않는 것"이
 * 하나 늘었다는 뜻이므로, 이 파일 밖에서 `getRandomValues`·`Math.random`을 부르지 마라.
 *
 * ## `randomUUID`를 쓰지 않는 이유
 *
 * `crypto.randomUUID()`는 보안 컨텍스트(https·localhost)에서만 존재한다. LAN IP + http로 폰을 붙이는
 * 플레이테스트에서는 `undefined`다(`input/editor.ts`의 id 발급기가 같은 이유로 폴백을 둔다).
 * `getRandomValues`는 비보안 컨텍스트에서도 있으므로 그것만 쓴다.
 *
 * ## 저장은 하지 않는다
 *
 * 저장은 `progress.ts`가 한 덩어리로 한다(핀·봉투·사물함과 함께 - 부분 실패를 막기 위해서다).
 * 이 파일은 `mint`를 **넘겨 줄 뿐**이고(`loadProgress(storage, mintAccountSeed)`), `progress.ts`는
 * 시드가 없을 때만 그것을 부른다. 그래서 이 함수가 몇 번 불리는지는 저장 상태가 정한다 -
 * 정상이면 계정당 한 번이다.
 */

/** 시드의 길이(바이트). 16바이트 = 128비트 - 우연히 겹칠 걱정이 없는 크기이면서 저장에 짧다(32자). */
export const ACCOUNT_SEED_BYTES = 16

/** 바이트를 채우는 함수. 테스트가 고정된 바이트를 넣어 서식을 검사하기 위해 주입한다. */
export type FillRandom = (bytes: Uint8Array<ArrayBuffer>) => void

/**
 * 기본 엔트로피원. `crypto`가 없는 환경(아주 옛 웹뷰)에서는 `Math.random`으로 떨어진다 -
 * 이 파일이 그 폴백을 허락받은 유일한 자리다(머리말). 그때도 시드는 시드다: 재현성은 시드에서 파생되는
 * 것들의 성질이고, 시드 자체가 예측 불가능해야 할 이유는 11.4절대로 없다(예측해서 얻는 것이 없다).
 */
function defaultFillRandom(bytes: Uint8Array<ArrayBuffer>): void {
  const api = globalThis.crypto
  if (api !== undefined && typeof api.getRandomValues === 'function') {
    api.getRandomValues(bytes)
    return
  }
  console.warn('[accountSeed] crypto.getRandomValues가 없다. Math.random으로 시드를 만든다.')
  for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
}

/**
 * 계정 시드를 하나 만든다. 소문자 16진수 32자.
 *
 * 16진수인 이유는 봉투 시드 문자열(`${accountSeed}:envelope:${seq}`)의 구분자 `:`와 절대 겹치지 않고,
 * 저장을 열어 본 사람이 읽어도 그것으로 할 수 있는 일이 없기 때문이다.
 */
export function mintAccountSeed(fill: FillRandom = defaultFillRandom): string {
  const bytes = new Uint8Array(ACCOUNT_SEED_BYTES)
  fill(bytes)
  let hex = ''
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0')
  return hex
}
